import type { GridApi } from '@mint-lib/ag-grid/community';
import { useTranslation } from '@mint-lib/i18n';
import { SendAlt, WarningAlt } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications';
import {
  RichTooltip,
  RichTooltipContent,
  RichTooltipTrigger,
} from '@mint-lib/tw-components';
import { Auth } from '@mint-sdk/api-auth';

import type { UserTableV2Row } from '../datasource.js';

type Props = {
  user: UserTableV2Row;
  api?: GridApi;
  isReadonlyPermission?: boolean;
};

const InvitationExpiredPopover = ({
  user,
  api,
  isReadonlyPermission,
}: Props) => {
  const { t } = useTranslation('@myn/permissions');
  const resendInvitation = Auth.resendUserInvitationV2();

  const getNode = () =>
    api && !api.isDestroyed() ? api.getRowNode(String(user.id)) : undefined;

  const handleResend = () => {
    if (resendInvitation.isPending) return;
    resendInvitation.mutate(
      { data: { id: user.id } },
      {
        onSuccess: (data) => {
          enqueueSnackbar({
            removable: true,
            variant: 'success',
            title: t('Success'),
            textLayout: 'multiline',
            subTitle: t('Invitation sent to user.'),
          });
          getNode()?.setData(data);
        },
        onError: () => {
          enqueueSnackbar({
            removable: true,
            variant: 'error',
            title: t('Failure'),
            textLayout: 'multiline',
            subTitle: t('Something went wrong. Please try again'),
          });
        },
      },
    );
  };

  return (
    <RichTooltip>
      <RichTooltipTrigger asChild>
        <button
          type="button"
          className="flex shrink-0 cursor-pointer items-center justify-center"
        >
          <WarningAlt
            aria-label={t('Invitation expired')}
            className="h-4 w-4 fill-warning-main"
            role="img"
          />
        </button>
      </RichTooltipTrigger>
      <RichTooltipContent
        align="end"
        side="bottom"
        className="flex w-fit flex-col gap-2"
      >
        <div className="flex items-center gap-2">
          <WarningAlt className="h-4 w-4 shrink-0 fill-warning-main" />
          <span>{t('Invitation expired')}</span>
        </div>
        {isReadonlyPermission ? null : (
          <button
            type="button"
            onClick={handleResend}
            disabled={resendInvitation.isPending}
            className="flex cursor-pointer items-center gap-2 text-tertiary-main disabled:cursor-not-allowed disabled:opacity-50"
          >
            <SendAlt className="h-4 w-4 shrink-0 fill-current" />
            <span>{t('Resend invitation')}</span>
          </button>
        )}
      </RichTooltipContent>
    </RichTooltip>
  );
};

export default InvitationExpiredPopover;
